import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

/**
 * Note: If a specific variant is not available in the design system,
 * please contact Matt to include it in the design system when added.
 */

/**
 * Note: Most text colors should use one of the colors defined in the root tailwind styles
 * in the tailwind-config package's styles.css file. These colors are designed to maintain
 * consistency across the application's design system for dark and light mode.
 *
 * Use other colors sparingly.
 */

// Shared text variant configuration that can be reused across components
export const textVariantConfig = {
  // Header
  h1: 'font-header font-bold text-[2rem] leading-[2.5rem]', // 32px / 40px
  'h1-normal': 'font-header font-medium text-[2rem] leading-[2.5rem]', // 32px / 40px
  h2: 'font-header font-bold text-[1.75rem] leading-[2.25rem]', // 28px / 36px
  'h2-normal': 'font-header font-medium text-[1.75rem] leading-[2.25rem]', // 28px / 36px
  h3: 'font-header font-bold text-[1.5rem] leading-[2rem]', // 24px / 32px
  'h3-normal': 'font-header font-medium text-[1.5rem] leading-[2rem]', // 24px / 32px
  h4: 'font-header font-bold text-[1.25rem] leading-[1.75rem]', // 20px / 28px
  'h4-normal': 'font-header font-medium text-[1.25rem] leading-[1.75rem]', // 20px / 28px
  h5: 'font-header font-bold text-[1.125rem] leading-[1.625rem]', // 18px / 26px
  'h5-normal': 'font-header font-medium text-[1.125rem] leading-[1.625rem]', // 18px / 26px
  h6: 'font-header font-bold text-base leading-[1.5rem]', // 16px / 24px
  h7: 'font-header font-bold text-[0.875rem] leading-[1.375rem]', // 14px / 22px
  'header-xl': 'font-header font-normal text-[1.1875rem]', // 19px, Regular weight
  'header-display-large': 'font-header font-bold text-[3.75rem]', // 60px
  'header-display': 'font-header font-bold text-[3rem] tracking-[-0.06rem]', // 48px
  'header-xs-caps': 'font-header font-normal text-[0.75rem] uppercase leading-[1rem] tracking-[0.1425rem]', // 12px
  // Base
  base: 'font-base font-normal text-base', // 16px
  'base-sb': 'font-base font-semibold text-base', // 16px
  'base-b': 'font-base font-bold text-base', // 16px
  'base-xl': 'font-base font-normal text-[1.375rem]', // 22px
  'base-lg': 'font-base font-normal text-[1.125rem]', // 18px
  'base-sm': 'font-base font-normal text-[0.875rem] leading-[1.125rem]', // 14px & 18px line height
  'base-xs': 'font-base font-normal text-[0.75rem]', // 12px
  'base-xss': 'font-base font-normal text-[0.625rem]', // 10px
  'lg-sb': 'font-base font-semibold text-[1.125rem]', // 18px
  'lg-b': 'font-base font-bold text-[1.125rem]', // 18px
  'sm-sb': 'font-base font-semibold text-[0.875rem]', // 14px
  'xs-sb': 'font-base font-semibold text-[0.75rem]', // 12px
  'xs-b': 'font-base font-bold text-[0.75rem]', // 12px
  'base-xs-caps': 'font-base font-normal text-[0.75rem] uppercase leading-[1rem] tracking-[0.27rem]', // 12px
  // Button
  'btn-lrg': 'font-base font-semibold text-base', // 16px
  'btn-sml': 'font-base font-semibold text-[0.875rem]', // 14px
  // Input
  input: 'font-number font-normal text-base', // 16px
  'input-filled': 'font-number font-semibold text-base', // 16px
  // Number
  'number-jumbo': 'font-number font-semibold text-[180px] leading-[160px] tracking-[-5.4px]', // 180px
  'number-3xxl': 'font-number font-bold text-[2.5rem]', // 40px
  'number-xxl': 'font-number font-bold text-[2.25rem]', // 36px
  'number-xl': 'font-number font-bold text-[1.875rem] tracking-[-0.9px]', // 30px
  'number-lg': 'font-number font-bold text-[1.5rem]', // 24px
  'number-md': 'font-number font-bold text-[1.25rem]', // 20px
  'number-base': 'font-number font-medium text-base', // 16px
  'number-sm': 'font-number font-medium text-[0.875rem]', // 14px
  'number-xs': 'font-number font-medium text-[0.75rem]', // 12px
  // Disclaimer
  disclaimer: 'font-base italic text-[0.875rem]', // 14px
} as const

const textVariants = cva('', {
  variants: {
    variant: textVariantConfig,
  },
  defaultVariants: {
    variant: 'base',
  },
})

export type TextVariant = VariantProps<typeof textVariants>['variant']

export interface TextProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof textVariants> {
  inline?: boolean
}

const Text = forwardRef<HTMLSpanElement, TextProps>(
  ({ className, variant, inline = false, children, ...props }, ref) => {
    const textClassName = cn(textVariants({ variant, className }))

    if (inline) {
      return (
        <span ref={ref} className={textClassName} {...props}>
          {children}
        </span>
      )
    }

    return (
      <p className={textClassName} {...props}>
        {children}
      </p>
    )
  },
)
Text.displayName = 'Text'

export { Text, textVariants }
